export default {
  title: 'App Studio',
  untitled: 'Untitled app',
  titleLabel: 'App name',
  renameFailed: 'The app could not be renamed. Try again.',
  loading: 'Loading the app',
  loadFailed: 'This app could not be loaded. It may have been deleted, or you may not have access to it.',
  backToApplications: 'Back to Applications',
  preview: {
    tabs: {
      preview: 'Preview',
      code: 'Code',
      data: 'Data'
    },
    frameTitle: 'App preview',
    loading: 'Loading the preview',
    failed: 'The preview could not be opened. Try reloading the page.',
    inspect: 'Pick an element to talk about',
    inspectCancel: 'Cancel picking',
    nav: {
      back: 'Back',
      forward: 'Forward',
      reload: 'Reload',
      address: "The app's address",
      mobile: 'Mobile width',
      openInNewTab: 'Open in a new tab'
    },
    building: 'Building your app',
    buildingIdle: 'Your app will appear here',
    buildingHint: 'Describe your app in the chat to get started.'
  },
  console: {
    title: 'Console',
    toggle: 'Show console',
    toggleWithProblems: 'Show console, new errors or warnings',
    clear: 'Clear the console',
    close: 'Hide console',
    empty: 'No runtime output yet. Errors, warnings and failed requests from the app will appear here.'
  },
  actions: 'App actions',
  attach: {
    menu: 'Attach',
    source: {
      item: 'Data source',
      title: 'Choose a table',
      confirm: 'Attach {count, plural, one {# table} other {# tables}}',
      tooltip: '{fqn} · {connection}'
    },
    map: {
      item: 'Map',
      title: 'Choose a map to rebuild as an app',
      untitled: 'Untitled map',
      tooltip: 'Map · {id}'
    },
    asset: {
      item: 'Visual asset',
      tooltip: 'Image · {name}',
      title: 'Add a visual asset',
      description: 'Upload an image the assistant can use in your app — a header logo, the favicon…',
      imageLabel: 'Image',
      browse: 'Browse',
      noFile: 'No file selected',
      hint: 'PNG, JPG, SVG, WEBP or GIF · up to 1 MB',
      unsupported: 'Unsupported file — use a PNG, JPG, SVG, WEBP or GIF image.',
      tooLarge: 'That image is too large — the limit is 1 MB.',
      dropTitle: 'Drop an image here',
      dropAria: 'Upload a visual asset',
      add: 'Add',
      cancel: 'Cancel',
      failed: "Couldn't attach the image. Try again."
    }
  },
  data: {
    none: 'This app does not use any data yet. Ask the assistant for a map of one of your tables.',
    loading: 'Loading the data this app uses',
    loadFailed: 'The data this app uses could not be loaded. Try reloading the page.',
    connectionUnused: 'Nothing declared on this connection yet.',
    usedData: 'Used data',
    selectSource: 'Select a source',
    preview: {
      failed: 'This source could not be read',
      openInDataExplorer: 'Open in Data Explorer'
    },
    kind: {
      query: 'Named source',
      table: 'Table'
    }
  },
  assistant: {
    title: 'Assistant',
    actions: 'Conversation actions',
    thinking: 'Thinking',
    working: 'Working',
    clear: 'Clear conversation',
    placeholder: 'Describe what you want to build…',
    welcome:
      'Describe the app you want and I will build it — a map of your data, styled, with the widgets and filters you ask for. Tell me which table to use, or ask me to find one.',
    selectModel: 'Select model',
    switchModel: {
      title: 'Switch the model?',
      message: 'The conversation starts again from scratch. What has been built stays as it is.',
      confirm: 'Switch model'
    },
    tools: {
      readFile: 'Reading',
      writeFile: 'Writing',
      editFile: 'Editing',
      deleteFile: 'Deleting',
      setAppTitle: 'Naming the app',
      readSkillReference: 'Reading the docs',
      readPreviewLogs: 'Reading the preview logs',
      listResources: 'Exploring data',
      executeQuery: 'Querying data',
      addConnection: 'Adding a connection',
      readAppSources: 'Reading the data sources',
      setAppSource: 'Adding a source',
      removeAppSource: 'Removing a source',
      readMap: 'Reading the map'
    }
  },
  versions: {
    open: 'Version history',
    title: 'Version history',
    empty: 'No versions yet. The assistant saves one each time it changes the app.',
    loading: 'Loading the version history',
    failed: 'The version history could not be loaded. Try reopening it.',
    selectPrompt: 'Select a version to see it running, without restoring it.',
    previewTitle: 'Preview of a past version',
    previewLoading: 'Loading this version',
    previewFailed: 'This version could not be opened.',
    changesSaved: 'Changes saved successfully',
    changes: {
      published: 'The app was published',
      added: 'Added {path}',
      modified: 'Changed {path}',
      removed: 'Removed {path}',
      none: 'No new changes'
    },
    restoreConfirm: {
      title: 'Restore this version?',
      message: "The app's files are replaced with the ones saved in this version.",
      unsaved: 'Changes made since the last saved version are lost.',
      confirm: 'Restore'
    },
    restored: 'Version restored',
    restoreFailed: 'This version could not be restored. Try again.'
  },
  apps: {
    sections: {
      draft: 'Draft',
      published: 'Published',
      examples: 'Examples',
      sharedTooltip: 'Shared with you'
    },
    count: '{count, plural, one {# app} other {# apps}}',
    loading: 'Loading apps',
    loadFailed: 'Your apps could not be loaded. Try reloading the page.',
    examples: {
      creating: 'Creating an app from {name}',
      createFailed: 'The app could not be created from this example. Try again.'
    },
    state: {
      draft: 'Draft',
      published: 'Published',
      hosted: 'Hosted app'
    },
    actions: {
      menu: 'Actions for {name}',
      duplicate: 'Duplicate',
      delete: 'Delete',
      deleteTitle: 'Delete this app?',
      deleteMessage: '"{name}" and every version of it will be deleted. This cannot be undone.',
      deletePublishedMessage:
        '"{name}" and every version of it will be deleted, and the published app will be taken offline. This cannot be undone.',
      duplicateFailed: 'This app could not be duplicated. Try again.',
      deleteFailed: 'This app could not be deleted. Try again.'
    },
    filters: {
      searchPlaceholder: 'Search apps',
      kind: {
        label: 'Filter by kind',
        all: 'All',
        hosted: 'Hosted apps'
      },
      owner: {
        label: 'Filter by owner',
        all: 'All',
        mine: 'My apps',
        organization: 'Organization'
      },
      order: {
        label: 'Sort',
        recent: 'Recently updated',
        name: 'A-Z'
      }
    },
    empty: {
      title: 'No apps yet',
      description: 'Apps shared with you will show up here.'
    },
    noResults: {
      title: 'No apps match',
      description: 'Try a different search, or clear the filters.'
    }
  },
  composer: {
    title: 'Start creating Geospatial apps with AI',
    subtitle: 'Use our new App Studio with your data to create and share awesome geospatial apps',
    placeholder: 'Describe your app and the data you want to use…',
    createFailed: 'The app could not be created. Try again.',
    creating: 'Creating your app',
    style: {
      open: 'Pick a style',
      title: 'Style',
      hint: 'Pick a starting look, or skip it — you can always ask the assistant to restyle later.'
    }
  },
  publish: {
    title: 'Publish app',
    published: 'Published app',
    action: 'Publish',
    update: 'Update',
    open: 'Open app',
    address: 'Address',
    addressHelp: 'Lowercase letters, numbers and hyphens. The app is served at /app/ followed by this address.',
    appUrl: 'App URL',
    appUrlHelp: 'Anyone you share the app with can open this link.',
    favicon: {
      label: 'App icon',
      hint: 'Shown in the browser tab · SVG',
      change: 'Change',
      svgOnly: 'The app icon must be an SVG.',
      notSvg: "That file doesn't look like an SVG.",
      tooLarge: 'That icon is too large — the limit is 512 KB.',
      failed: 'The app icon could not be saved. Try again.'
    },
    sharing: 'Sharing',
    yourOrganization: 'your organization',
    publishedState: {
      title: 'This app is published',
      message: 'Copy the link below to share it, or change who can access it. Update to publish the current draft.'
    },
    unpublishedChanges: {
      badge: 'Unpublished changes',
      title: 'You have unpublished changes',
      message:
        'A newer version has been saved since this app was published. Update to publish the current draft, or copy the link below to share what is currently live.'
    },
    failed: 'This app could not be published',
    unpublish: {
      action: 'Unpublish',
      title: 'Unpublish app',
      message: 'This takes the app offline at its URL.',
      again: 'You can publish it again at any time.',
      confirm: 'Unpublish',
      failed: 'This app could not be unpublished. Try again.'
    }
  },
  files: {
    empty: 'No files yet',
    title: 'Files',
    notYet: 'This app has no files yet. They will appear here as the assistant writes them.',
    loading: "Loading the app's files",
    listFailed: "This app's files could not be loaded. Try reloading the page.",
    selectPrompt: 'Select a file to read it.',
    reading: 'Reading the file',
    readFailed: 'This file could not be read. Try selecting it again.'
  }
}
